import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, CreditCard, LayoutDashboard, Plus } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/lib/i18n";
import { useSeo, useHreflang } from "@/components/seo/Seo";
import { LANGS, STATIC_COPY, S, p } from "@/lib/seoContent";
import { lp } from "@/lib/seoContent";

const ease = [0.22, 1, 0.36, 1];
const SHOTS = {
  overview: "/business/overview.webp",
  newride: "/business/newride.webp",
  detail: "/business/rides-detail.webp",
  invoices: "/business/invoices.webp",
};

const FadeIn = ({ children, delay = 0, y = 24, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const Shot = ({ src, alt, testId, className = "" }) => (
  <div data-testid={testId}
    className={`overflow-hidden rounded-2xl border border-ink/10 bg-white shadow-[0_45px_90px_-42px_rgba(11,31,77,0.5)] ${className}`}>
    <div className="flex items-center gap-1.5 border-b border-ink/5 bg-cloud px-4 py-2.5">
      <span className="h-2 w-2 rounded-full bg-ink/15" />
      <span className="h-2 w-2 rounded-full bg-ink/15" />
      <span className="h-2 w-2 rounded-full bg-ink/15" />
      <span className="ml-3 hidden rounded-md bg-white px-3 py-1 font-mono text-[9px] tracking-wide text-slate sm:block">
        gomiles.ch/business/app
      </span>
    </div>
    <img src={src} alt={alt} loading="lazy" className="img-clean w-full object-cover object-top" />
  </div>
);

const Feature = ({ data, img, reverse = false, alt, dark = false, testId, index }) => (
  <section className={`py-20 sm:py-24 lg:py-32 ${dark ? "bg-cloud" : "bg-white"}`} data-testid={testId}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <FadeIn className={reverse ? "lg:order-2" : ""}>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-miles">
              {String(index).padStart(2, "0")} — {data.tag}
            </p>
            <h2 className="mt-4 max-w-md text-3xl font-semibold leading-[1.05] tracking-[-0.04em] text-ink sm:text-4xl lg:text-5xl">
              {data.t}
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-slate">{data.d}</p>
            <ul className="mt-7 space-y-3">
              {data.points.map((point) => (
                <li key={point} className="flex items-center gap-3 text-sm font-medium text-ink/85">
                  <Check className="h-4 w-4 shrink-0 text-miles" strokeWidth={2} /> {point}
                </li>
              ))}
            </ul>
          </FadeIn>
          <FadeIn delay={0.12} className={reverse ? "lg:order-1" : ""}>
            <Shot src={img} alt={alt} testId={`${testId}-shot`} />
          </FadeIn>
        </div>
      </div>
    </section>
);

const ROLE_ICONS = [LayoutDashboard, Plus, CreditCard];

export default function Business() {
  const { t, lang } = useLang();
  const seoPage = STATIC_COPY.business[lang] || STATIC_COPY.business.fr;
  useSeo({ lang, title: seoPage.title, description: seoPage.meta, path: p(lang, "business"), type: "business", id: "" });
  useHreflang(Object.fromEntries(LANGS.map((l) => [l, p(l, "business")])));
  return (
    <div className="min-h-screen bg-white text-ink" data-testid="business-page">
      <Navbar />

      <section className="grain relative overflow-hidden bg-miles-deep pb-0 pt-32 lg:pt-40" data-testid="business-hero">
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-miles/25 blur-3xl" />
        <div className="pointer-events-none absolute -left-40 top-1/3 h-96 w-96 rounded-full bg-miles-electric/10 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn y={16}>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("biz.eyebrow")}</p>
            <h1 className="mt-6 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl lg:text-6xl">
              {t("biz.h1a")}
              <br />
              <span className="font-light text-white/60">{t("biz.h1b")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t("biz.sub")}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/business/inscription" data-testid="business-hero-cta"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-sm font-semibold text-miles-deep transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-ice">
                {t("biz.cta1")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/business/connexion" data-testid="business-hero-demo"
                className="inline-flex items-center gap-2 rounded-full border border-miles-sky/30 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-miles-sky">
                {t("biz.cta2")}
              </Link>
            </div>
          </FadeIn>
          <FadeIn delay={0.15} className="relative mt-14 lg:mt-16">
            <div className="translate-y-10 lg:translate-y-14">
              <Shot src={SHOTS.overview} alt={t("biz.shot_alt")} testId="business-hero-shot"
                className="mx-auto max-w-5xl shadow-[0_60px_120px_-40px_rgba(4,17,46,0.8)]" />
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="pb-8 pt-24 lg:pt-32" data-testid="business-proof">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <div className="grid gap-8 border-b border-ink/10 pb-10 sm:grid-cols-3">
              {t("biz.proof").map(([value, label]) => (
                <div key={label}>
                  <p className="text-3xl font-semibold tracking-[-0.04em] text-ink sm:text-4xl">{value}</p>
                  <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate">{label}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate">{t("biz.proof_note")}</p>
          </FadeIn>
        </div>
      </section>

      <div className="pt-14 lg:pt-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("biz.feat_eyebrow")}</p>
          </FadeIn>
        </div>
      </div>

      <Feature data={t("biz.f1")} img={SHOTS.newride} alt={t("biz.f1.alt")} testId="biz-feature-booking" index={1} />
      <Feature data={t("biz.f2")} img={SHOTS.detail} alt={t("biz.f2.alt")} testId="biz-feature-rides" index={2} reverse dark />
      <Feature data={t("biz.f3")} img={SHOTS.invoices} alt={t("biz.f3.alt")} testId="biz-feature-invoicing" index={3} />

      <section className="bg-cloud py-24 lg:py-32" data-testid="business-roles">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("biz.roles_eyebrow")}</p>
            <h2 className="mt-5 text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-5xl">
              {t("biz.roles_h2a")}{" "}
              <span className="font-light text-slate/55">{t("biz.roles_h2b")}</span>
            </h2>
          </FadeIn>
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {t("biz.roles").map(([name, desc], i) => {
              const Icon = ROLE_ICONS[i];
              return (
                <FadeIn key={name} delay={i * 0.08} className="h-full">
                  <div className="h-full rounded-[1.75rem] border border-ink/10 bg-white p-7 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-miles/30 hover:shadow-[0_24px_60px_-32px_rgba(36,87,255,0.3)]"
                    data-testid={`business-role-${["admin", "booker", "finance"][i]}`}>
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-miles-ice text-miles">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <p className="mt-5 text-lg font-semibold tracking-tight text-ink">{name}</p>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate">{desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="business-workflow">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles">{t("biz.flow_eyebrow")}</p>
            <h2 className="mt-5 max-w-2xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-ink sm:text-5xl">{t("biz.flow_h2")}</h2>
          </FadeIn>
          <div className="mt-12 border-t border-ink/10">
            {t("biz.flow").map(([title, desc], i) => (
              <FadeIn key={title} delay={i * 0.05} y={16}>
                <div className="group grid gap-2 border-b border-ink/10 py-8 sm:grid-cols-[64px_1fr_1.1fr] sm:items-baseline sm:gap-10"
                  data-testid={`workflow-step-${i}`}>
                  <span className="font-mono text-xs font-semibold tracking-[0.2em] text-miles">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-xl font-semibold tracking-tight text-ink transition-transform duration-300 group-hover:translate-x-1.5 sm:text-2xl">{title}</p>
                  <p className="text-sm leading-relaxed text-slate">{desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={0.1}>
            <Link to="/business/inscription" data-testid="business-workflow-demo"
              className="group mt-9 inline-flex items-center gap-2 text-sm font-semibold text-miles transition-colors hover:text-miles-dark">
              {t("biz.flow_cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5} />
            </Link>
          </FadeIn>
        </div>
      </section>

      <section className="grain relative overflow-hidden bg-miles-deep py-24 lg:py-32" data-testid="business-final">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-miles/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 left-1/4 h-80 w-80 rounded-full bg-miles-electric/15 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn>
            <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-miles-sky">{t("biz.final_eyebrow")}</p>
            <h2 className="mt-5 max-w-3xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-white sm:text-5xl lg:text-6xl">
              {t("biz.final_h2a")}
              <br />
              <span className="font-light text-white/60">{t("biz.final_h2b")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t("biz.final_p")}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to={lp(lang, "/contact")} data-testid="business-final-demo"
                className="inline-flex items-center gap-2 rounded-full bg-miles px-8 py-4 text-sm font-semibold text-white shadow-[0_18px_45px_rgba(36,87,255,0.4)] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-miles-dark">
                {t("biz.final_cta1")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </Link>
              <Link to="/business/inscription" data-testid="business-final-signup"
                className="inline-flex items-center gap-2 rounded-full border border-miles-sky/30 px-8 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:border-miles-sky">
                {t("biz.final_cta2")}
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <Footer />
    </div>
  );
}
